import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/useLanguage";
import { isoWeekNumber } from "./dateUtils";

interface WeekNavProps {
  start: Date;
  end: Date;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
}

function fmt(d: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(d);
}

export function WeekNav({ start, end, onPrev, onNext, onToday }: WeekNavProps) {
  const { t } = useTranslation("calendar");
  const { language, rtl } = useLanguage();
  // C5: in RTL, "previous" is the right-pointing chevron because the
  // timeline reads right-to-left. Swap the icon component so the arrow
  // tip aligns with the action regardless of language.
  const PrevIcon = rtl ? ChevronRight : ChevronLeft;
  const NextIcon = rtl ? ChevronLeft : ChevronRight;
  return (
    <div className="flex items-center gap-3">
      <Button
        variant="ghost"
        size="sm"
        aria-label={t("nav.prevWeek")}
        onClick={onPrev}
      >
        <PrevIcon className="h-4 w-4" data-testid="weeknav-prev-icon" />
      </Button>
      <div className="text-sm font-medium tabular-nums">
        {fmt(start, language)} – {fmt(end, language)}, {end.getUTCFullYear()}
      </div>
      <Button
        variant="ghost"
        size="sm"
        aria-label={t("nav.nextWeek")}
        onClick={onNext}
      >
        <NextIcon className="h-4 w-4" data-testid="weeknav-next-icon" />
      </Button>
      <Button variant="outline" size="sm" onClick={onToday}>
        {t("nav.today")}
      </Button>
      <span className="text-xs text-muted-foreground">
        {t("nav.isoWeek", { value: isoWeekNumber(start) })}
      </span>
    </div>
  );
}
